'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslations } from 'next-intl';

import ProfilePredictionDialog from '@/app/[locale]/(main)/profile/_components/ProfilePredictionDialog';
import { OverlayLoading } from '@/components/shared/OverlayLoading';
import { useClaimRefunds } from '@/hooks/useClaimRefunds';
import { useClaimWinnings } from '@/hooks/useClaimWinnings';
import { usePoolTransactions } from '@/hooks/usePoolTransactions';
import { usePredictionHistory } from '@/hooks/usePredictionHistory';
import { useSectionState } from '@/hooks/useSectionState';
import { useRouter } from '@/i18n/navigation';
import { I_PredictionHistoryResponse } from '@/server/actions/user/getPredictionHistory.action';
import { I_UserPoolBalance } from '@/server/actions/user/getUserPoolBalances.action';
import { I_UserPoolTransactionsResponse } from '@/server/actions/user/getUserPoolTransactions.action';
import { T_UserBlockchainData } from '@/ts/types/T_UserBlockchainData';

import { ActionButtons } from './components/ActionButtons';
import { HistoryFilters } from './components/HistoryFilters';
import { HistoryList } from './components/HistoryList';
import { InfoCardsSection } from './components/InfoCardsSection';
import { PresaleCountdown } from './components/PresaleCountdown';
import { usePredictionHistoryTransformer } from './hooks/usePredictionHistoryTransformer';

interface I_HistoryWrapperProps {
  variant: 'predictions' | 'pools';
  presaleEndDate?: string;
  blockchainData?: T_UserBlockchainData;
  initialPredictionHistory?: I_PredictionHistoryResponse | null;
  poolBalances?: I_UserPoolBalance[];
  initialPoolTransactions?: I_UserPoolTransactionsResponse | null;
  poolTokenSymbol?: string;
  poolAddress?: string;
}

function HistoryWrapper({
  variant,
  presaleEndDate,
  blockchainData,
  initialPredictionHistory,
  poolBalances,
  initialPoolTransactions,
  poolTokenSymbol,
  poolAddress
}: I_HistoryWrapperProps) {
  const t = useTranslations();
  const router = useRouter();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [filterValue, setFilterValue] = useState<string>('all');
  const [sortValue, setSortValue] = useState<string>('newest');
  const [selectedTokens, setSelectedTokens] = useState<string[]>([]);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);

  // Reset filters on mount and on back/forward navigation (popstate)
  useEffect(() => {
    const resetFilters = () => {
      setFilterValue('all');
      setSortValue('newest');
      setSelectedTokens([]);
    };

    // Reset on mount (handles forward navigation + Router Cache restoration)
    resetFilters();

    // Reset on browser back/forward button
    window.addEventListener('popstate', resetFilters);
    return () => window.removeEventListener('popstate', resetFilters);
  }, []);

  const isPools = variant === 'pools';
  const isPredictions = variant === 'predictions';

  const orderDirection = sortValue === 'newest' ? 'desc' : 'asc';

  const outcomeFilter = useMemo(() => {
    if (!isPredictions || filterValue === 'all' || filterValue === 'canceled')
      return undefined;
    if (filterValue === 'won') return 'WON';
    if (filterValue === 'lost') return 'LOST';
    if (filterValue === 'ongoing') return 'PENDING';
    return undefined;
  }, [isPredictions, filterValue]);

  const canceledParam = useMemo(() => {
    if (!isPredictions) return undefined;
    if (filterValue === 'canceled') return true;
    return undefined;
  }, [isPredictions, filterValue]);

  // Convert selected tokens array to comma-separated string
  const tokensParam = useMemo(() => {
    if (!isPredictions || selectedTokens.length === 0) return undefined;
    return selectedTokens.join(',');
  }, [isPredictions, selectedTokens]);

  // Determine transaction type filter for pools
  const transactionType =
    isPools && filterValue !== 'all'
      ? (filterValue.toUpperCase() as 'BUY' | 'SELL')
      : undefined;

  const {
    data: predictionHistoryData,
    total: _predictionHistoryTotal,
    hasMore: hasMorePredictions,
    isLoading: isLoadingPredictions,
    handleLoadMore: handleLoadMorePredictions
  } = usePredictionHistory({
    tokens: tokensParam,
    outcome: outcomeFilter,
    orderBy: 'lastSubmissionTime',
    orderDirection,
    initialData: initialPredictionHistory,
    canceled: canceledParam
  });

  // Extract available token symbols from prediction history response events
  // This must be after usePredictionHistory hook to access predictionHistoryData
  const availableTokenSymbols = useMemo(() => {
    if (!isPredictions) return undefined;

    const tokenSymbols = new Set<string>();

    // Get from initial prediction history response events
    if (
      initialPredictionHistory?.events &&
      initialPredictionHistory.events.length > 0
    ) {
      initialPredictionHistory.events.forEach(participation => {
        if (participation.predictionEvent.tokenSymbol) {
          tokenSymbols.add(participation.predictionEvent.tokenSymbol);
        }
      });
    }
    return Array.from(tokenSymbols).length > 0
      ? Array.from(tokenSymbols)
      : undefined;
  }, [initialPredictionHistory?.events, isPredictions]);

  const {
    data: poolTransactionsData,
    total: _poolTransactionsTotal,
    hasMore: hasMorePoolTransactions,
    poolsData,
    isLoading: isLoadingPoolTransactions,
    handleLoadMore: handleLoadMorePoolTransactions
  } = usePoolTransactions({
    poolTokenSymbol,
    poolAddress,
    orderBy: 'blockTimestamp',
    orderDirection,
    type: transactionType,
    initialData: initialPoolTransactions
  });

  const isEmptyState = isPredictions
    ? !blockchainData?.predictionEventParticipations?.length &&
      !predictionHistoryData.length
    : isPools
      ? !poolTransactionsData.length
      : false;

  const {
    isClaiming,
    setIsClaiming,
    claimingParticipationId,
    setClaimingParticipationId,
    defaultTab,
    setDefaultTab,
    modalType,
    setModalType,
    modalVariant,
    setModalVariant,
    predictionAddress,
    setPredictionAddress,
    predictionId,
    setPredictionId,
    selectedParticipation,
    setSelectedParticipation,
    isModalOpen,
    setIsModalOpen
  } = useSectionState();

  const { handleClaimWin } = useClaimWinnings({
    isClaiming,
    setIsClaiming,
    setClaimingParticipationId
  });

  const { handleClaimRefund } = useClaimRefunds({
    isClaiming,
    setIsClaiming,
    setClaimingParticipationId
  });

  const transformedPredictionData = usePredictionHistoryTransformer(
    predictionHistoryData,
    claimingParticipationId || undefined
  );

  useEffect(() => {
    const handleScroll = () => {
      if (scrollRef.current) {
        setIsScrolled(scrollRef.current.scrollTop > 0);
      }
    };

    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  // Handlers for history list actions
  const handleViewWinners = (item: (typeof transformedPredictionData)[0]) => {
    setDefaultTab('prime');
    setModalType('winners');
    setModalVariant(item.tokenVariant);
    setPredictionAddress(
      item.predictionEvent.predictionContract.contractAddress
    );
    setPredictionId(item.predictionEvent.predictionId);
    setSelectedParticipation({
      predictionEvent: item.predictionEvent,
      hasWon: item.participation.hasWon,
      hasClaimed: item.participation.hasClaimed,
      claimData: item.participation.claimData
    });
    setIsModalOpen(true);
  };
  const handleViewMissedPrediction = (
    item: (typeof transformedPredictionData)[0]
  ) => {
    setModalType('missed');
    setModalVariant(item.tokenVariant);
    setPredictionAddress(
      item.predictionEvent.predictionContract.contractAddress
    );
    setPredictionId(item.predictionEvent.predictionId);
    setSelectedParticipation({
      predictionEvent: item.predictionEvent,
      lostAmountDecimal: item.participation.lostAmountDecimal
    });
    setIsModalOpen(true);
  };

  const handleViewOngoing = (item: (typeof transformedPredictionData)[0]) => {
    setModalType(item.status === 'resolving' ? 'resolving' : 'ongoing');
    setModalVariant(item.tokenVariant);
    setPredictionAddress(
      item.predictionEvent.predictionContract.contractAddress
    );
    setPredictionId(item.predictionEvent.predictionId);
    setSelectedParticipation({
      predictionEvent: item.predictionEvent,
      lostAmountDecimal: item.participation.lostAmountDecimal
    });
    setIsModalOpen(true);
  };

  const claimingText = isClaiming
    ? t('profile.predictionWinners.claiming') || 'Claiming...'
    : '';

  return (
    <>
      <OverlayLoading isVisible={isClaiming} text={claimingText} />
      <div className="flex flex-col md:flex-row gap-11 p-4 md:p-0">
        <div className="flex-1 h-fit relative top-4 md:sticky">
          <InfoCardsSection
            variant={variant}
            isEmptyState={isEmptyState}
            blockchainData={blockchainData}
            poolBalances={poolBalances}
          />
          {isPools && <PresaleCountdown presaleEndDate={presaleEndDate} />}
          <ActionButtons
            variant={variant}
            presaleEndDate={presaleEndDate}
            poolSlug={poolBalances?.[0]?.pool.slug}
          />
        </div>
        <div className="flex-1">
          <HistoryFilters
            variant={variant}
            filterValue={filterValue}
            sortValue={sortValue}
            selectedTokens={selectedTokens}
            availableTokenSymbols={availableTokenSymbols}
            onFilterChange={setFilterValue}
            onSortChange={setSortValue}
            onTokensChange={setSelectedTokens}
          />
          <HistoryList
            variant={variant}
            transformedPredictionData={transformedPredictionData}
            poolTransactionsData={isPools ? poolTransactionsData : undefined}
            poolsData={isPools ? poolsData : undefined}
            isScrolled={isScrolled}
            scrollRef={scrollRef}
            isLoadingPredictions={isLoadingPredictions}
            hasMorePredictions={hasMorePredictions}
            isLoadingPoolTransactions={isLoadingPoolTransactions}
            hasMorePoolTransactions={hasMorePoolTransactions}
            blockchainData={blockchainData}
            onLoadMore={handleLoadMorePredictions}
            onLoadMorePoolTransactions={handleLoadMorePoolTransactions}
            onClaimWin={handleClaimWin}
            onClaimRefund={handleClaimRefund}
            onViewWinners={handleViewWinners}
            onViewMissedPrediction={handleViewMissedPrediction}
            onViewOngoing={handleViewOngoing}
          />
        </div>
        {isPredictions && (
          <ProfilePredictionDialog
            open={isModalOpen}
            onOpenChange={setIsModalOpen}
            defaultTab={defaultTab}
            type={modalType}
            variant={modalVariant}
            predictionAddress={predictionAddress}
            predictionId={predictionId}
            selectedParticipation={selectedParticipation}
            onClaimingStateChange={() => {}}
            onClaimSuccess={() => {
              router.refresh();
            }}
          />
        )}
      </div>
    </>
  );
}

export default HistoryWrapper;
